import type { Currency, PlanType, SocialPlatform, TierLimits } from '../types';

export const API_VERSION = 'v1' as const;
export const API_BASE_PATH = `/api/${API_VERSION}` as const;

/** Brand name is the primary user-visible identifier; keep it concise. */
export const BRAND_NAME_MAX_LENGTH = 100;

export const API_ROUTES = {
  SUBSCRIPTION_ME: `${API_BASE_PATH}/subscriptions/me`,
  SUBSCRIPTION_CANCEL: `${API_BASE_PATH}/subscriptions/cancel`,
  PAYMENT_INITIATE: `${API_BASE_PATH}/payments/initiate`,
  PAYMENT_HISTORY: `${API_BASE_PATH}/payments/history`,
  PAYMENT_IPN: `${API_BASE_PATH}/payments/ipn`,

  BRANDS: `${API_BASE_PATH}/brands`,
  BRAND: (brandId: string) => `${API_BASE_PATH}/brands/${brandId}`,

  CONTENT_GENERATE: `${API_BASE_PATH}/content/generate`,
  CONTENT_BULK_GENERATE: `${API_BASE_PATH}/content/bulk-generate`,

  POSTS: `${API_BASE_PATH}/posts`,
  POST: (postId: string) => `${API_BASE_PATH}/posts/${postId}`,
  POST_SUBMIT: (postId: string) => `${API_BASE_PATH}/posts/${postId}/submit`,
  POST_APPROVE: (postId: string) => `${API_BASE_PATH}/posts/${postId}/approve`,
  POST_REJECT: (postId: string) => `${API_BASE_PATH}/posts/${postId}/reject`,
  POST_VARIANT: (postId: string, variantId: string) =>
    `${API_BASE_PATH}/posts/${postId}/variants/${variantId}`,
  POST_VARIANT_SCHEDULE: (postId: string, variantId: string) =>
    `${API_BASE_PATH}/posts/${postId}/variants/${variantId}/schedule`,
  POST_VARIANT_CANCEL: (postId: string, variantId: string) =>
    `${API_BASE_PATH}/posts/${postId}/variants/${variantId}/cancel`,
  POST_VARIANT_PUBLISH_NOW: (postId: string, variantId: string) =>
    `${API_BASE_PATH}/posts/${postId}/variants/${variantId}/publish-now`,

  SOCIAL_ACCOUNTS: `${API_BASE_PATH}/social/accounts`,
  SOCIAL_DISCONNECT: (accountId: string) =>
    `${API_BASE_PATH}/social/accounts/${accountId}/disconnect`,
  SOCIAL_REFRESH: (accountId: string) => `${API_BASE_PATH}/social/accounts/${accountId}/refresh`,

  // The generic connect flow. Platform-agnostic on purpose: `{platform}` is the
  // only thing that varies, and adding a provider adds no route here.
  OAUTH_INITIALIZE: (platform: SocialPlatform) =>
    `${API_BASE_PATH}/oauth/${platform}/initialize`,
  /** The provider redirects the browser here. Necessarily unauthenticated. */
  OAUTH_CALLBACK: (platform: SocialPlatform) => `${API_BASE_PATH}/oauth/${platform}/callback`,
  /** GET reads the discovered targets; POST finalizes the chosen ones. */
  OAUTH_CONNECTION: (connectionId: string) =>
    `${API_BASE_PATH}/oauth/connections/${connectionId}`,

  MEDIA: `${API_BASE_PATH}/media`,
  MEDIA_UPLOAD_URL: `${API_BASE_PATH}/media/upload-url`,
  MEDIA_COMPLETE: (mediaId: string) => `${API_BASE_PATH}/media/${mediaId}/complete`,
  MEDIA_ITEM: (mediaId: string) => `${API_BASE_PATH}/media/${mediaId}`,

  ANALYTICS_OVERVIEW: `${API_BASE_PATH}/analytics/overview`,
  ANALYTICS_POSTS: `${API_BASE_PATH}/analytics/posts`,

  // Tenant-scoped. Every one of these carries the tenant in the path rather than
  // in a body or a header: the path is what the route wrapper reads to resolve
  // the caller's membership before the handler runs, and a tenant id taken from
  // a body is somebody else's tenant id waiting to happen.
  TENANTS: `${API_BASE_PATH}/tenants`,
  TENANT_CONTEXT: (tenantId: string) => `${API_BASE_PATH}/tenants/${tenantId}/context`,
  TENANT_MEMBERS: (tenantId: string) => `${API_BASE_PATH}/tenants/${tenantId}/members`,
  TENANT_MEMBER: (tenantId: string, userId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/members/${userId}`,
  TENANT_MEMBER_ROLE: (tenantId: string, userId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/members/${userId}/role`,
  TENANT_MEMBER_PERMISSIONS: (tenantId: string, userId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/members/${userId}/permissions`,
  TENANT_MEMBER_TEAM_VISIBILITY: (tenantId: string, userId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/members/${userId}/team-visibility`,
  TENANT_INVITATIONS: (tenantId: string) => `${API_BASE_PATH}/tenants/${tenantId}/invitations`,
  TENANT_INVITATION: (tenantId: string, invitationId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/invitations/${invitationId}`,
  TENANT_TEAMS: (tenantId: string) => `${API_BASE_PATH}/tenants/${tenantId}/teams`,
  TENANT_TEAM: (tenantId: string, teamId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/teams/${teamId}`,
  TENANT_TEAM_MEMBERS: (tenantId: string, teamId: string) =>
    `${API_BASE_PATH}/tenants/${tenantId}/teams/${teamId}/members`,
  TENANT_SUBSCRIPTION: (tenantId: string) => `${API_BASE_PATH}/tenants/${tenantId}/subscription`,
  // Serves the tenant admin and the superadmin from one handler: a superadmin
  // resolves into every tenant holding the whole permission catalog, so a second
  // admin-only analytics endpoint would be the same query behind a second door.
  TENANT_STATS: (tenantId: string) => `${API_BASE_PATH}/tenants/${tenantId}/stats`,

  // Superadmin-only, because none of these can be expressed as a permission a
  // tenant member could hold: creating a tenant has no tenant to be a member of,
  // and deleting one is not something its own admin may do.
  ADMIN_TENANTS: `${API_BASE_PATH}/admin/tenants`,
  ADMIN_TENANT: (tenantId: string) => `${API_BASE_PATH}/admin/tenants/${tenantId}`,
  ADMIN_TENANT_ADMIN: (tenantId: string) => `${API_BASE_PATH}/admin/tenants/${tenantId}/admin`,
  ADMIN_TENANT_PLAN: (tenantId: string) => `${API_BASE_PATH}/admin/tenants/${tenantId}/plan`,

  ADMIN_USERS: `${API_BASE_PATH}/admin/users`,
  ADMIN_ACTIVITY: `${API_BASE_PATH}/admin/activity`,
  ADMIN_STATS: `${API_BASE_PATH}/admin/stats`,
  ADMIN_USER_BAN: (userId: string) => `${API_BASE_PATH}/admin/users/${userId}/ban`,
  ADMIN_USER_UNBAN: (userId: string) => `${API_BASE_PATH}/admin/users/${userId}/unban`,
} as const;

/** Offered in the admin ban dialog; any 1–3650 day value is accepted by the API. */
export const BAN_DURATION_DAYS = [1, 7, 30] as const;

/** Caps `pageSize` on the admin list endpoints so a hand-written query cannot pull the whole table. */
export const ADMIN_PAGE_SIZE_MAX = 100;
export const ADMIN_PAGE_SIZE_DEFAULT = 25;

const ALL_PLATFORMS: readonly SocialPlatform[] = [
  'facebook',
  'instagram',
];

/**
 * The plan matrix. The single source of truth for what a plan may do —
 * PricingPage renders from it and every quota check reads it.
 *
 * `pro_trial` and `pro` are the retired question-generator plans. They map onto
 * the starter and growth limits so existing subscribers keep a working product
 * for the rest of their period without being silently upgraded or downgraded.
 * They are absent from PURCHASABLE_PLANS, so nobody new can land on one.
 */
export const TIER_LIMITS: Record<PlanType, TierLimits> = {
  free_trial: {
    brands: 1,
    connectedAccounts: 2,
    postsPerMonth: 10,
    aiGenerationsPerMonth: 30,
    mediaStorageMb: 200,
    scheduleHorizonDays: 14,
    approvalWorkflow: false,
    analyticsRetentionDays: 30,
    // TikTok and YouTube are withheld from the trial deliberately: both have
    // per-project publishing quotas, so a free tier would burn a limit shared
    // with paying customers.
    platforms: ['facebook', 'instagram'],
  },
  starter: {
    brands: 1,
    connectedAccounts: 3,
    postsPerMonth: 60,
    aiGenerationsPerMonth: 150,
    mediaStorageMb: 2_000,
    scheduleHorizonDays: 30,
    approvalWorkflow: false,
    analyticsRetentionDays: 90,
    platforms: ['facebook', 'instagram'],
  },
  growth: {
    brands: 3,
    connectedAccounts: 10,
    postsPerMonth: 250,
    aiGenerationsPerMonth: 600,
    mediaStorageMb: 10_000,
    scheduleHorizonDays: 90,
    approvalWorkflow: true,
    analyticsRetentionDays: 365,
    platforms: ALL_PLATFORMS,
  },
  agency: {
    brands: 25,
    connectedAccounts: 50,
    postsPerMonth: 1_500,
    aiGenerationsPerMonth: 4_000,
    mediaStorageMb: 100_000,
    scheduleHorizonDays: 365,
    approvalWorkflow: true,
    analyticsRetentionDays: 730,
    platforms: ALL_PLATFORMS,
  },
  institution: {
    brands: 100,
    connectedAccounts: 200,
    postsPerMonth: 10_000,
    aiGenerationsPerMonth: 20_000,
    mediaStorageMb: 500_000,
    scheduleHorizonDays: 365,
    approvalWorkflow: true,
    analyticsRetentionDays: 730,
    platforms: ALL_PLATFORMS,
  },

  // Retired plans, mapped onto their nearest current equivalent.
  pro_trial: {
    brands: 1,
    connectedAccounts: 3,
    postsPerMonth: 60,
    aiGenerationsPerMonth: 150,
    mediaStorageMb: 2_000,
    scheduleHorizonDays: 30,
    approvalWorkflow: false,
    analyticsRetentionDays: 90,
    platforms: ['facebook', 'instagram'],
  },
  pro: {
    brands: 3,
    connectedAccounts: 10,
    postsPerMonth: 250,
    aiGenerationsPerMonth: 600,
    mediaStorageMb: 10_000,
    scheduleHorizonDays: 90,
    approvalWorkflow: true,
    analyticsRetentionDays: 365,
    platforms: ALL_PLATFORMS,
  },
};

export const PLAN_DURATIONS: Record<PlanType, number> = {
  free_trial: 14,
  starter: 30,
  growth: 30,
  agency: 30,
  institution: 30,
  pro_trial: 30,
  pro: 30,
};

/**
 * Prices per currency. The charge is ALWAYS taken from here and never from the
 * request — a client that asks to pay 1 unit for the agency plan is asking, not
 * telling.
 */
export const PLAN_PRICES: Record<'starter' | 'growth' | 'agency', Record<Currency, number>> = {
  starter: { BDT: 999, USD: 15 },
  growth: { BDT: 2_499, USD: 39 },
  agency: { BDT: 7_999, USD: 129 },
};

export const PLAN_NAMES: Record<PlanType, string> = {
  free_trial: 'Free Trial',
  starter: 'Starter',
  growth: 'Growth',
  agency: 'Agency',
  institution: 'Enterprise',
  pro_trial: 'Pro Trial (legacy)',
  pro: 'Pro (legacy)',
};

export const PURCHASABLE_PLANS = ['starter', 'growth', 'agency'] as const;
export type PurchasablePlan = (typeof PURCHASABLE_PLANS)[number];

/** BDT is settled through SSLCommerz; everything else goes to Stripe. */
export const GATEWAY_BY_CURRENCY: Record<Currency, 'sslcommerz' | 'stripe'> = {
  BDT: 'sslcommerz',
  USD: 'stripe',
};
